import { EXPENSE_TYPE_IRREGULAR, EXPENSE_TYPE_ONGOING, normalizeExpenseType } from "../../lib/expenseType.js";

const monthLabelKeys = [
  "january",
  "february",
  "march",
  "april",
  "mayFull",
  "june",
  "july",
  "august",
  "september",
  "october",
  "november",
  "december"
];
const presetDefinitions = [
  {
    value: "month-category-supplier",
    groupBy: ["month", "category", "supplier"]
  },
  {
    value: "month-type-category-supplier",
    groupBy: ["month", "type", "category", "supplier"]
  },
  {
    value: "month-method-category-supplier",
    groupBy: ["month", "method", "category", "supplier"]
  },
  {
    value: "category-supplier-month",
    groupBy: ["category", "supplier", "month"]
  },
  {
    value: "category-month-supplier",
    groupBy: ["category", "month", "supplier"]
  },
  {
    value: "supplier-category-month",
    groupBy: ["supplier", "category", "month"]
  },
  {
    value: "supplier-month-category",
    groupBy: ["supplier", "month", "category"]
  },
  {
    value: "category-supplier-matrix",
    groupBy: ["category", "supplier"],
    variant: "matrix",
    labelKey: "matrix"
  }
];

const presetDefinitionByKey = new Map(presetDefinitions.map((preset) => [preset.value, preset]));

function parseExpenseDate(dateText) {
  if (!dateText) {
    return null;
  }

  const parsed = new Date(`${dateText}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

function getMonthIndex(dateText) {
  const parsedDate = parseExpenseDate(dateText);
  return parsedDate ? parsedDate.getMonth() : -1;
}

function normalizeText(value, fallback) {
  const normalized = String(value ?? "").trim();
  return normalized || fallback;
}

function createGroupingDescriptor(row, groupingKey, t = (key) => key) {
  if (groupingKey === "month") {
    const parsedDate = parseExpenseDate(row.date);
    if (!parsedDate) {
      return {
        key: "month:unknown",
        label: t("unknownMonth"),
        sortKey: "9999-99"
      };
    }

    const monthKey = `${parsedDate.getFullYear()}-${String(parsedDate.getMonth() + 1).padStart(2, "0")}`;
    return {
      key: `month:${monthKey}`,
      label: t(monthLabelKeys[parsedDate.getMonth()]),
      sortKey: monthKey
    };
  }

  if (groupingKey === "category") {
    const label = normalizeText(row.category, t("uncategorized"));
    return {
      key: `category:${label.toLocaleLowerCase()}`,
      label,
      sortKey: label.toLocaleLowerCase()
    };
  }

  if (groupingKey === "type") {
    const normalizedType = normalizeExpenseType(row.type);
    const label = normalizedType === EXPENSE_TYPE_ONGOING
      ? t("ongoing")
      : normalizedType === EXPENSE_TYPE_IRREGULAR
        ? t("irregular")
        : normalizeText(row.type, t("unknownType"));
    return {
      key: `type:${normalizedType.toLocaleLowerCase()}`,
      label,
      sortKey: label.toLocaleLowerCase()
    };
  }

  if (groupingKey === "method") {
    const label = normalizeText(row.method, t("unknownMethod"));
    return {
      key: `method:${label.toLocaleLowerCase()}`,
      label,
      sortKey: label.toLocaleLowerCase()
    };
  }

  if (groupingKey === "supplier") {
    const label = normalizeText(row.supplier, t("unknownSupplier"));
    return {
      key: `supplier:${label.toLocaleLowerCase()}`,
      label,
      sortKey: label.toLocaleLowerCase()
    };
  }

  throw new Error(`Unsupported explorer grouping key: ${groupingKey}`);
}

function sortGroupedEntries(entries, groupingKey) {
  if (groupingKey === "month") {
    return entries.sort((left, right) => left.descriptor.sortKey.localeCompare(right.descriptor.sortKey));
  }

  return entries.sort((left, right) => {
    if (right.amount !== left.amount) {
      return right.amount - left.amount;
    }

    return left.descriptor.label.localeCompare(right.descriptor.label);
  });
}

function toPercent(value, total) {
  if (total <= 0) {
    return 0;
  }

  return (value / total) * 100;
}

function buildNodes(rows, groupBy, level, parentAmount, parentPath = [], t = (key) => key) {
  const groupingKey = groupBy[level];
  const groupedEntries = rows.reduce((groups, row) => {
    const descriptor = createGroupingDescriptor(row, groupingKey, t);
    const existing = groups.get(descriptor.key);
    const amount = Number(row.amount ?? 0);
    const monthIndex = getMonthIndex(row.date);

    if (existing) {
      existing.rows.push(row);
      existing.amount += amount;
      if (monthIndex >= 0) {
        existing.monthTotals[monthIndex] += amount;
      }
      return groups;
    }

    const monthTotals = new Array(12).fill(0);
    if (monthIndex >= 0) {
      monthTotals[monthIndex] = amount;
    }

    groups.set(descriptor.key, {
      descriptor,
      rows: [row],
      amount,
      monthTotals
    });
    return groups;
  }, new Map());

  return sortGroupedEntries([...groupedEntries.values()], groupingKey).map((entry) => {
    const percent = toPercent(entry.amount, parentAmount);
    const path = [...parentPath, entry.descriptor.key];
    const hasChildren = level < groupBy.length - 1;

    return {
      id: path.join("|"),
      label: entry.descriptor.label,
      sortKey: entry.descriptor.sortKey,
      amount: entry.amount,
      percent,
      monthTotals: entry.monthTotals,
      maxMonthAmount: Math.max(...entry.monthTotals, 0),
      children: hasChildren
        ? buildNodes(entry.rows, groupBy, level + 1, entry.amount, path, t)
        : undefined
    };
  });
}

function flattenNodes(nodes, expandedIds, depth = 0) {
  return nodes.flatMap((node) => {
    const expanded = expandedIds.has(node.id);
    const currentRow = [{
      ...node,
      depth,
      expanded
    }];

    if (!node.children?.length || !expanded) {
      return currentRow;
    }

    return currentRow.concat(flattenNodes(node.children, expandedIds, depth + 1));
  });
}

function compareExplorerNodes(left, right, sortState) {
  if (!sortState) {
    return 0;
  }

  if (sortState.key === "group") {
    return String(left.sortKey ?? left.label ?? "").localeCompare(String(right.sortKey ?? right.label ?? ""));
  }

  if (sortState.key === "spent") {
    return left.amount - right.amount || left.label.localeCompare(right.label);
  }

  if (sortState.key === "share" || sortState.key === "meter") {
    return left.percent - right.percent || left.label.localeCompare(right.label);
  }

  return 0;
}

function sortExplorerNodes(nodes, sortState) {
  if (!sortState) {
    return nodes;
  }

  const sortedNodes = nodes
    .map((node) => ({
      ...node,
      children: node.children?.length ? sortExplorerNodes(node.children, sortState) : node.children
    }))
    .sort((left, right) => compareExplorerNodes(left, right, sortState));

  return sortState.direction === "asc" ? sortedNodes : sortedNodes.reverse();
}

function filterNodesByQuery(nodes, query) {
  const normalizedQuery = query.trim().toLocaleLowerCase();

  if (!normalizedQuery) {
    return nodes;
  }

  return nodes.flatMap((node) => {
    const matchesSelf = node.label.toLocaleLowerCase().includes(normalizedQuery);

    if (matchesSelf) {
      return [node];
    }

    if (!node.children?.length) {
      return [];
    }

    const matchingChildren = filterNodesByQuery(node.children, normalizedQuery);
    if (matchingChildren.length === 0) {
      return [];
    }

    return [{
      ...node,
      children: matchingChildren
    }];
  });
}

function seedExpandedIds(nodes) {
  const expandedIds = new Set();
  let currentNode = nodes[0];

  while (currentNode?.children?.length) {
    expandedIds.add(currentNode.id);
    currentNode = currentNode.children[0];
  }

  return expandedIds;
}

export function listAnalysisExplorerPresets(t = (key) => key, separator = "▸") {
  return presetDefinitions.map((preset) => ({
    ...preset,
    label: preset.labelKey
      ? t(preset.labelKey)
      : preset.groupBy.map((groupingKey) => t(groupingKey)).join(` ${separator} `)
  }));
}

export function getAnalysisExplorerGroupKeyAtDepth(presetKey, depth) {
  const preset = presetDefinitionByKey.get(presetKey) ?? presetDefinitions[0];
  if (!Number.isInteger(depth) || depth < 0 || depth >= preset.groupBy.length) {
    return null;
  }

  return preset.groupBy[depth] ?? null;
}

export function isAnalysisExplorerMatrixPreset(presetKey) {
  return (presetDefinitionByKey.get(presetKey) ?? presetDefinitions[0]).variant === "matrix";
}

export function buildAnalysisExplorerTree(expenseRows, presetKey, t = (key) => key) {
  const preset = presetDefinitionByKey.get(presetKey) ?? presetDefinitions[0];
  const normalizedRows = expenseRows.filter((row) => Number.isFinite(Number(row.amount)));
  const totalAmount = normalizedRows.reduce((sum, row) => sum + Number(row.amount ?? 0), 0);

  return buildNodes(normalizedRows, preset.groupBy, 0, totalAmount, [], t);
}

export function buildAnalysisExplorerRows(expenseRows, presetKey, expandedIds, t = (key) => key, sortState = null) {
  const tree = sortExplorerNodes(buildAnalysisExplorerTree(expenseRows, presetKey, t), sortState);
  return flattenNodes(tree, expandedIds);
}

export function buildAnalysisExplorerSearchRows(expenseRows, presetKey, query, t = (key) => key, sortState = null) {
  const tree = sortExplorerNodes(buildAnalysisExplorerTree(expenseRows, presetKey, t), sortState);
  const filteredTree = filterNodesByQuery(tree, query);
  const autoExpandedIds = new Set();

  function collectExpandableIds(nodes) {
    nodes.forEach((node) => {
      if (node.children?.length) {
        autoExpandedIds.add(node.id);
        collectExpandableIds(node.children);
      }
    });
  }

  collectExpandableIds(filteredTree);

  return flattenNodes(filteredTree, autoExpandedIds);
}

export function createInitialExpandedByPreset(expenseRows) {
  return presetDefinitions.reduce((accumulator, preset) => {
    accumulator[preset.value] = seedExpandedIds(buildAnalysisExplorerTree(expenseRows, preset.value));
    return accumulator;
  }, {});
}
